// src/utils/availabilityCalculator.ts
import {
    NonWorkingDay,
    PersonalAvailability,
    PersonalAvailabilityWeek
} from '../types';
import {
    getWeekDateRange,
    getWeekInfo,
    getMondayFromGlobalWeek
} from '../lib/week-calculator';

export interface WeekAvailability {
    globalWeekNumber: number;
    availableHours: number;
    workingDaysAffected: number;
}

/**
 * Calculate the number of available working hours for a given week.
 * Uses PersonalAvailabilityWeek lookup for simplified calculation.
 *
 * @param weekNumber - The global week number
 * @param baseHoursPerWeek - Base hours per week (e.g., 40)
 * @param defaultHoursPerDay - Default hours per day (e.g., 8)
 * @param nonWorkingDays - Company-wide non-working days
 * @param personId - Optional person ID to check personal availability
 * @param personalAvailabilityWeeks - Optional PersonalAvailabilityWeek records
 * @returns Available hours for the week
 */
export const getAvailableHoursForWeek = (
    weekNumber: number,
    baseHoursPerWeek: number,
    defaultHoursPerDay: number,
    nonWorkingDays: Record<string, NonWorkingDay>,
    personId?: string,
    personalAvailabilityWeeks?: Record<string, PersonalAvailabilityWeek>
): number => {
    let availableHours = baseHoursPerWeek;

    // First, check for PersonalAvailabilityWeek record if person is specified
    if (personId && personalAvailabilityWeeks) {
        const personalWeek = Object.values(personalAvailabilityWeeks).find(
            pw => pw.personId === personId && pw.globalWeekNumber === weekNumber
        );

        if (personalWeek) {
            // Use the pre-calculated available hours from the week record
            availableHours = personalWeek.availableHours;
        }
    }

    // Subtract company non-working day hours
    const {startDate, endDate} = getWeekDateRange(weekNumber);
    let companyHoursLost = 0;

    for (const day of Object.values(nonWorkingDays)) {
        const nwdStart = new Date(day.startDate);
        const nwdEnd = new Date(day.endDate);

        // Check if this non-working day overlaps with the week
        if (nwdStart <= endDate && nwdEnd >= startDate) {
            const currentDate = new Date(
                Math.max(nwdStart.getTime(), startDate.getTime())
            );
            const lastDate = new Date(
                Math.min(nwdEnd.getTime(), endDate.getTime())
            );

            while (currentDate <= lastDate) {
                const dayOfWeek = currentDate.getDay();
                if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                    // Monday to Friday only
                    const hoursAvailable = day.hoursPerDay ?? 0;
                    companyHoursLost += defaultHoursPerDay - hoursAvailable;
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }
        }
    }

    return Math.max(0, availableHours - companyHoursLost);
};

/**
 * Calculate weekly availability from a PersonalAvailability period.
 * Only considers Monday-Friday (weekends are excluded).
 */
export function calculateWeeklyAvailabilityFromPeriod(
    startDate: Date,
    endDate: Date,
    hoursPerDay: number | null,
    baseHoursPerDay: number
): WeekAvailability[] {
    const results: WeekAvailability[] = [];

    // Normalize dates to midnight
    const periodStart = new Date(startDate);
    periodStart.setHours(0, 0, 0, 0);

    const periodEnd = new Date(endDate);
    periodEnd.setHours(23, 59, 59, 999);

    // Get the week range
    const startWeekInfo = getWeekInfo(periodStart);
    const endWeekInfo = getWeekInfo(periodEnd);

    // Iterate through all weeks in the range
    for (
        let weekNum = startWeekInfo.globalWeekNumber;
        weekNum <= endWeekInfo.globalWeekNumber;
        weekNum++
    ) {
        const monday = getMondayFromGlobalWeek(weekNum);

        // Calculate Sunday of this week
        const sunday = new Date(monday);
        sunday.setDate(sunday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);

        // Calculate the overlap between the period and this week's Mon-Fri
        let availableHours = 0;
        let workingDaysAffected = 0;

        // Check each day Monday (0) through Friday (4)
        for (let dayOffset = 0; dayOffset < 5; dayOffset++) {
            const currentDay = new Date(monday);
            currentDay.setDate(currentDay.getDate() + dayOffset);
            currentDay.setHours(12, 0, 0, 0); // Set to noon to avoid DST issues

            // Check if this day falls within the availability period
            if (currentDay >= periodStart && currentDay <= periodEnd) {
                workingDaysAffected++;

                // Add hours for this day
                if (hoursPerDay === null) {
                    // Full time off - 0 hours available
                    availableHours += 0;
                } else {
                    // Partial or full availability
                    availableHours += hoursPerDay;
                }
            } else {
                // Day not affected by this period - use base hours
                availableHours += baseHoursPerDay;
            }
        }

        results.push({
            globalWeekNumber: weekNum,
            availableHours,
            workingDaysAffected
        });
    }

    return results;
}

/**
 * Calculate all affected weeks from a PersonalAvailability record.
 */
export function calculateWeeksFromAvailability(
    availability: PersonalAvailability,
    baseHoursPerDay: number
): WeekAvailability[] {
    return calculateWeeklyAvailabilityFromPeriod(
        new Date(availability.startDate),
        new Date(availability.endDate),
        availability.hoursPerDay,
        baseHoursPerDay
    );
}

/**
 * Get the list of global week numbers covered by a date range.
 */
export function getWeekNumbersInRange(
    startDate: Date,
    endDate: Date
): number[] {
    const startWeekInfo = getWeekInfo(startDate);
    const endWeekInfo = getWeekInfo(endDate);

    const weekNumbers: number[] = [];
    for (
        let weekNum = startWeekInfo.globalWeekNumber;
        weekNum <= endWeekInfo.globalWeekNumber;
        weekNum++
    ) {
        weekNumbers.push(weekNum);
    }

    return weekNumbers;
}

/**
 * Generate PersonalAvailabilityWeek records from a PersonalAvailability date range.
 * Calculates available hours for each affected week based on date overlap.
 *
 * @param personalAvailability - The PersonalAvailability record with date range
 * @param _baseHoursPerWeek - Base hours per week (e.g., 40) - not currently used
 * @param defaultHoursPerDay - Default hours per day (e.g., 8)
 * @param source - Whether this is 'calculated' (from leave) or 'manual' (from grid)
 * @returns Array of PersonalAvailabilityWeek records to create/update
 */
export function generatePersonalAvailabilityWeeks(
    personalAvailability: PersonalAvailability,
    _baseHoursPerWeek: number,
    defaultHoursPerDay: number,
    source: 'calculated' | 'manual' = 'calculated'
): Omit<PersonalAvailabilityWeek, 'id'>[] {
    const results: Omit<PersonalAvailabilityWeek, 'id'>[] = [];

    // Get all affected weeks
    const weekNumbers = getWeekNumbersInRange(
        new Date(personalAvailability.startDate),
        new Date(personalAvailability.endDate)
    );

    // Calculate available hours for each week
    for (const weekNumber of weekNumbers) {
        const {startDate: weekStart} = getWeekDateRange(weekNumber);

        const periodStart = new Date(personalAvailability.startDate);
        periodStart.setHours(0, 0, 0, 0);

        const periodEnd = new Date(personalAvailability.endDate);
        periodEnd.setHours(23, 59, 59, 999);

        // Calculate available hours for this week
        let weekAvailableHours = 0;

        // Check each weekday (Mon-Fri)
        for (let dayOffset = 0; dayOffset < 5; dayOffset++) {
            const currentDay = new Date(weekStart);
            currentDay.setDate(currentDay.getDate() + dayOffset);
            currentDay.setHours(12, 0, 0, 0); // Set to noon to avoid DST issues

            // Check if this day falls within the availability period
            if (currentDay >= periodStart && currentDay <= periodEnd) {
                // This day is affected by the personal availability
                if (personalAvailability.hoursPerDay === null) {
                    // Full time off - 0 hours available
                    weekAvailableHours += 0;
                } else {
                    // Partial or full availability
                    weekAvailableHours += personalAvailability.hoursPerDay;
                }
            } else {
                // Day not affected - use base hours
                weekAvailableHours += defaultHoursPerDay;
            }
        }

        results.push({
            personalAvailabilityId: personalAvailability.id,
            personId: personalAvailability.personId,
            globalWeekNumber: weekNumber,
            availableHours: weekAvailableHours,
            source,
            description: personalAvailability.description,
            lastModified: new Date().toISOString()
        });
    }

    return results;
}
